import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../../supabaseClient";
import {
  Check, X, Clock, Search, Globe, Instagram, Facebook, Mail, Phone, Calendar, Link2,
} from "lucide-react";
import { useAdminNotifications } from "./AdminNotificationsContext";
import { getVendorSocials } from "./socialLinks";
import "./AdminSignupRequests.css";

const TABS = [
  { key: "pending",  label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "denied",   label: "Denied" },
];

const PLATFORM_ICON = {
  website:   Globe,
  instagram: Instagram,
  facebook:  Facebook,
  tiktok:    Link2,
  twitter:   Link2,
  youtube:   Link2,
  linkedin:  Link2,
  whatsapp:  Link2,
};

const PLATFORM_LABEL = {
  website: "Website",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  twitter: "X / Twitter",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
};

const AdminSignupRequests = () => {
  const { markSignupRequestsSeen } = useAdminNotifications();
  const [activeTab, setActiveTab] = useState("pending");
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [actioningId, setActioningId] = useState(null);
  const [search, setSearch] = useState("");

  const loadRequests = async (status) => {
    setLoading(true);
    setError("");
    const { data, error: err } = await supabase
      .from("vendor_signup_requests")
      .select("*")
      .eq("status", status)
      .order("created_at", { ascending: false });

    if (err) {
      console.error("Failed to load signup requests:", err.message);
      setError("Couldn't load requests.");
      setRequests([]);
    } else {
      setRequests(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadRequests(activeTab);
  }, [activeTab]);

  // Clears the sidebar badge every time this tab is opened — runs once on
  // mount regardless of which sub-tab (pending/approved/denied) is active.
  useEffect(() => {
    markSignupRequestsSeen();
  }, [markSignupRequestsSeen]);

  // Clear the search when switching tabs so a stale filter from "Pending"
  // doesn't silently hide everything when you jump to "Approved".
  useEffect(() => {
    setSearch("");
  }, [activeTab]);

  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((r) => {
      const haystack = [r.brand_name, r.contact_person, r.email, r.phone_number]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [requests, search]);

  // Approve creates the real auth.users account and `vendors` row, then
  // emails the vendor plain login instructions ("log in, click Forgot
  // password"). No recovery/invite link is ever generated or emailed —
  // that's the exact mechanism that gets silently consumed by Gmail's
  // link-prefetch scanner before the vendor opens the email, same bug
  // just fixed in api/accept-vendor.js. This mirrors that fix.
  const handleApprove = async (request) => {
    setActioningId(request.id);
    setError("");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated as admin.");

      const res = await fetch("/api/approve-vendor-signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ requestId: request.id }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to approve vendor");

      setRequests((prev) => prev.filter((r) => r.id !== request.id));
    } catch (err) {
      console.error("Approve failed:", err.message);
      setError(err.message || "Couldn't approve this request. Try again.");
    } finally {
      setActioningId(null);
    }
  };

  const handleDeny = async (request) => {
    const reason = window.prompt("Optional: reason for declining (shown internally only)");
    if (reason === null) return; // cancelled

    setActioningId(request.id);
    const { data: { user } } = await supabase.auth.getUser();

    const { error: err } = await supabase
      .from("vendor_signup_requests")
      .update({
        status: "denied",
        reviewed_at: new Date().toISOString(),
        reviewed_by: user?.id || null,
        denial_reason: reason || null,
      })
      .eq("id", request.id);

    setActioningId(null);

    if (err) {
      console.error("Deny failed:", err.message);
      setError("Couldn't decline this request. Try again.");
      return;
    }

    setRequests((prev) => prev.filter((r) => r.id !== request.id));
  };

  return (
    <div className="asr-page">
      <div className="asr-header">
        <h1 className="asr-title">Vendor signup requests</h1>
        <p className="asr-subtitle">Review and approve new vendor account requests.</p>
      </div>

      <div className="asr-toolbar">
        <div className="asr-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`asr-tab ${activeTab === tab.key ? "asr-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="asr-search">
          <Search size={15} className="asr-search-icon" />
          <input
            type="text"
            placeholder="Search brand, contact, email, or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="asr-search-clear"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {error && <p className="asr-error">{error}</p>}

      {loading ? (
        <p className="asr-status-text">Loading…</p>
      ) : requests.length === 0 ? (
        <div className="asr-empty">
          <Clock size={24} />
          <p>No {activeTab} requests.</p>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="asr-empty">
          <Search size={24} />
          <p>No {activeTab} requests match "{search}".</p>
        </div>
      ) : (
        <>
          <p className="asr-result-count">
            {filteredRequests.length} of {requests.length} {activeTab} request{requests.length !== 1 ? "s" : ""}
            {search && " (filtered)"}
          </p>

          <div className="asr-card-grid">
            {filteredRequests.map((req) => {
              const socials = getVendorSocials({
                website: req.website,
                instagram: req.instagram,
                facebook: req.facebook,
              });

              return (
                <div key={req.id} className="asr-card">
                  <div className="asr-card-head">
                    <div className="asr-card-avatar">
                      {(req.brand_name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="asr-card-heading">
                      <h3>{req.brand_name || "Unnamed brand"}</h3>
                      {req.contact_person && <span>{req.contact_person}</span>}
                    </div>
                  </div>

                  <div className="asr-card-meta">
                    <div className="asr-card-row">
                      <Mail size={13} />
                      <a href={`mailto:${req.email}`}>{req.email}</a>
                    </div>
                    {req.phone_number && (
                      <div className="asr-card-row">
                        <Phone size={13} />
                        <a href={`tel:${req.phone_number}`}>{req.phone_number}</a>
                      </div>
                    )}
                    <div className="asr-card-row asr-card-row--muted">
                      <Calendar size={13} />
                      <span>
                        {new Date(req.created_at).toLocaleDateString("en-KE", {
                          day: "numeric", month: "short", year: "numeric",
                        })}
                      </span>
                    </div>
                  </div>

                  {socials.length > 0 && (
                    <div className="asr-card-socials">
                      {socials.map((s, i) => {
                        const Icon = PLATFORM_ICON[s.platform] || Link2;
                        const chipLabel = s.platform === "website" ? s.label : PLATFORM_LABEL[s.platform];
                        return s.url ? (
                          <a
                            key={i}
                            href={s.url}
                            target="_blank"
                            rel="noreferrer"
                            className="asr-social-chip"
                            title={s.url}
                          >
                            <Icon size={13} /> {chipLabel}
                          </a>
                        ) : (
                          <span key={i} className="asr-social-chip asr-social-chip--static" title={s.label}>
                            <Icon size={13} /> {chipLabel}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {activeTab === "pending" && (
                    <div className="asr-card-actions">
                      <button
                        type="button"
                        className="asr-approve-btn"
                        onClick={() => handleApprove(req)}
                        disabled={actioningId === req.id}
                      >
                        <Check size={14} /> {actioningId === req.id ? "Approving…" : "Approve"}
                      </button>
                      <button
                        type="button"
                        className="asr-deny-btn"
                        onClick={() => handleDeny(req)}
                        disabled={actioningId === req.id}
                      >
                        <X size={14} /> Deny
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminSignupRequests;